/**
 * "Novo candidato" modal — starts a tracked candidate from a LinkedIn profile.
 * Round 1 is always the structured LinkedIn profile (the only score-moving
 * evidence today); it's created `evaluating` and scored in the background, so on
 * success we route straight to the timeline where the live evaluation shows.
 */

import type { LevelAssessmentModel } from "@shared/labs/level-assessment-model";
import { Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { rpcClient } from "@/lib/rpc";
import {
  C,
  ErrorText,
  Field,
  GhostButton,
  ModalShell,
  OverlayActions,
  OverlayHeader,
  PrimaryButton,
  TextInput,
  UI,
} from "./candidate-ui";

interface CreateResult {
  candidate: { id: string };
}

export function NewCandidateModal({
  modelId,
  model,
  onClose,
  onCreated,
}: {
  modelId: string;
  model: LevelAssessmentModel | null;
  onClose: () => void;
  onCreated: (candidateId: string) => void;
}) {
  const [name, setName] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [linkedinUrl, setLinkedinUrl] = useState("");
  const [disciplineId, setDisciplineId] = useState<string>(model?.disciplines[0]?.id ?? "");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const disciplines = model?.disciplines ?? [];
  const needsDiscipline = disciplines.length > 0;

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  async function onSubmit() {
    setError(null);
    const trimmedName = name.trim();
    const url = linkedinUrl.trim();
    if (!trimmedName) return setError("Dê um nome ao candidato.");
    if (!url) return setError("Cole o perfil do LinkedIn.");
    if (needsDiscipline && !disciplineId) return setError("Escolha uma disciplina.");

    setSubmitting(true);
    try {
      const res = (await rpcClient.labs.levelAssessmentCandidateCreate({
        name: trimmedName,
        subtitle: subtitle.trim() || undefined,
        modelId,
        disciplineId: needsDiscipline ? disciplineId : undefined,
        linkedinUrl: url,
      })) as CreateResult;
      onCreated(res.candidate.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Falha ao criar o candidato.");
      setSubmitting(false);
    }
  }

  const selectStyle: React.CSSProperties = {
    width: "100%",
    ...UI,
    fontSize: 14,
    color: C.ink,
    background: C.white,
    border: `1px solid ${C.ink16}`,
    borderRadius: 10,
    padding: "11px 13px",
    outline: "none",
    cursor: "pointer",
  };

  return (
    <ModalShell onClose={onClose}>
      <OverlayHeader
        title="Novo candidato"
        subtitle="Todo monitoramento começa com um perfil do LinkedIn — a única evidência que hoje move o score."
        onClose={onClose}
      />
      <Field label="Nome">
        <TextInput value={name} onChange={setName} placeholder="ex.: Ana Beatriz Nunes" />
      </Field>
      <Field
        label="Perfil do LinkedIn"
        hint="Cole o link completo ou apenas o username. Vira o Round 1 · Triagem inicial."
      >
        <TextInput
          value={linkedinUrl}
          onChange={setLinkedinUrl}
          placeholder="linkedin.com/in/anabnunes"
        />
      </Field>
      <Field label="Subtítulo (opcional)">
        <TextInput
          value={subtitle}
          onChange={setSubtitle}
          placeholder="ex.: Staff Engineer @ Nubank"
        />
      </Field>
      {needsDiscipline && (
        <Field label="Disciplina">
          <select
            value={disciplineId}
            onChange={(e) => setDisciplineId(e.target.value)}
            style={selectStyle}
          >
            {disciplines.map((d) => (
              <option key={d.id} value={d.id}>
                {d.label}
              </option>
            ))}
          </select>
        </Field>
      )}
      {error && <ErrorText>{error}</ErrorText>}
      <OverlayActions>
        <GhostButton onClick={onClose} disabled={submitting}>
          Cancelar
        </GhostButton>
        <PrimaryButton onClick={onSubmit} disabled={submitting}>
          <Plus size={16} /> {submitting ? "Criando…" : "Criar e avaliar"}
        </PrimaryButton>
      </OverlayActions>
    </ModalShell>
  );
}
